import { Link } from 'react-router-dom';
import lumadriveIcon from '../assets/LumaDrive_Icon.png';
import './Terms.css';
import './Support.css';

const categories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        q: 'How do I start recording?',
        a: (
          <p>
            Open the app and tap the red record button in the centre of the screen. LumaDrive will
            begin recording immediately. Keep the app open and your screen on (or use Guided
            Access) for continuous recording while driving.
          </p>
        ),
      },
      {
        q: 'Does the screen need to stay on?',
        a: (
          <p>
            Yes. iOS restricts camera access when an app is fully backgrounded. Keep the app in
            the foreground while driving. You can reduce screen brightness to save battery.
          </p>
        ),
      },
      {
        q: 'Can I use the front camera?',
        a: (
          <p>
            Yes. Tap the camera-flip icon in the HUD to switch between the rear and front camera.
            The clip filename will include <code>_front</code> so you can tell them apart in your
            recordings list.
          </p>
        ),
      },
    ],
  },
  {
    id: 'trial-premium',
    title: 'Free Trial & Premium',
    items: [
      {
        q: 'How much free recording time do I get?',
        a: (
          <p>
            Every new install includes 30 minutes of free recording time. This is cumulative
            across all sessions — it does not reset.
          </p>
        ),
      },
      {
        q: 'What happens when the free trial runs out?',
        a: (
          <p>
            Recording stops. You can still view, organise, share, export, and delete all existing
            clips without a subscription. Only the ability to record new footage requires Premium.
          </p>
        ),
      },
      {
        q: 'What does LumaDrive Premium include?',
        a: (
          <ul className="support-list">
            <li>Unlimited recording time</li>
            <li>Loop recording (oldest unlocked clips auto-deleted when storage is full)</li>
            <li>All future premium features</li>
          </ul>
        ),
      },
      {
        q: 'What are the Premium options?',
        a: (
          <ul className="support-list">
            <li>
              <strong>Monthly</strong> — $3.99/month, auto-renewing, cancel any time
            </li>
            <li>
              <strong>Lifetime Unlock</strong> — $29.99 one-time payment, no recurring charges
            </li>
          </ul>
        ),
      },
      {
        q: 'How do I cancel my monthly subscription?',
        a: (
          <p>
            Go to iOS Settings → [your name] → Subscriptions → LumaDrive → Cancel Subscription.
            Cancel at least 24 hours before your next renewal date to avoid being charged for the
            next period.
          </p>
        ),
      },
      {
        q: 'I bought Premium but the app still shows the free tier. Help.',
        a: (
          <p>
            Tap Settings → Restore Purchases. If that doesn't resolve it, sign out and back into
            your Apple ID and try again. Contact support if the issue persists.
          </p>
        ),
      },
      {
        q: 'Is the Lifetime Unlock shared across my devices?',
        a: (
          <p>
            Yes. It is tied to your Apple ID. Tap Restore Purchases on any device signed into the
            same Apple ID to unlock it there.
          </p>
        ),
      },
    ],
  },
  {
    id: 'recording-loop',
    title: 'Recording & Loop Storage',
    items: [
      {
        q: 'What is loop recording?',
        a: (
          <p>
            When your device storage is nearly full, LumaDrive automatically deletes the oldest
            unlocked clips to make room for new footage. Locked clips are never auto-deleted.
          </p>
        ),
      },
      {
        q: 'How do I protect an important clip from being deleted?',
        a: (
          <p>
            Swipe left on the clip in the Recordings list and tap Lock, or open the clip and tap
            the lock icon. Locked clips are excluded from loop deletion.
          </p>
        ),
      },
      {
        q: 'How long are my clips?',
        a: (
          <p>
            Each clip is approximately 3 minutes long by default. Recording continues seamlessly
            into new clips.
          </p>
        ),
      },
      {
        q: 'My recording stopped unexpectedly. Why?',
        a: (
          <>
            <p>Common causes:</p>
            <ul className="support-list">
              <li>A phone call was received (iOS pauses camera access during calls)</li>
              <li>Another app requested camera access</li>
              <li>Storage was completely full (including locked clips)</li>
              <li>The device got too hot and throttled performance</li>
              <li>The app was swiped away from the foreground</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    id: 'incident',
    title: 'Incident Detection (Beta)',
    items: [
      {
        q: 'What is incident detection?',
        a: (
          <p>
            The app monitors the accelerometer for sudden impacts. When a significant jolt is
            detected, it automatically locks the current clip so it won't be loop-deleted.
          </p>
        ),
      },
      {
        q: 'Why is it labelled Beta?',
        a: (
          <p>
            Incident detection is experimental. It may miss real events or trigger on bumps,
            potholes, and speed humps. Do not rely on it as your only method of preserving footage
            after an incident. Lock clips manually after any event you want to keep.
          </p>
        ),
      },
      {
        q: 'It triggered when I went over a speed bump. Is that normal?',
        a: (
          <p>
            Yes. The threshold is intentionally sensitive to avoid missing real incidents. False
            positives are expected.
          </p>
        ),
      },
    ],
  },
  {
    id: 'clips-organisation',
    title: 'Clips & Organisation',
    items: [
      {
        q: 'How do I find a specific clip?',
        a: (
          <p>
            Clips are listed in reverse chronological order in the Recordings tab. You can also
            add tags to clips (swipe → Tag) and filter by tag using the filter button at the top.
          </p>
        ),
      },
      {
        q: 'What is the Archive?',
        a: (
          <p>
            Archiving moves a clip out of the main Recordings list into a separate Archive tab.
            Archived clips are kept but hidden from the main view. Useful for clips you want to
            keep but don't need to see every day.
          </p>
        ),
      },
      {
        q: 'What is the Trash?',
        a: (
          <p>
            Deleted clips are moved to Trash and remain there until you empty it. This gives you a
            chance to recover accidental deletions.
          </p>
        ),
      },
      {
        q: 'How do I export a clip?',
        a: (
          <p>
            Swipe left on a clip → Share, or open the clip and tap the share icon. You can save to
            Photos, share via AirDrop, or send through any app in the iOS share sheet.
          </p>
        ),
      },
    ],
  },
  {
    id: 'privacy-data',
    title: 'Privacy & Data',
    items: [
      {
        q: 'Does LumaDrive upload my footage anywhere?',
        a: (
          <p>
            No. All recordings stay on your device. Nothing is uploaded to the developer or any
            third party — ever.
          </p>
        ),
      },
      {
        q: 'Does the app collect any personal data?',
        a: (
          <p>
            No analytics, tracking, or advertising SDKs are included. The only external
            communication is with Apple for purchase verification, which Apple handles directly.
          </p>
        ),
      },
      {
        q: 'Where are clips stored on my device?',
        a: (
          <p>
            Clips are stored in the app's private Documents directory. They do not appear in your
            Photos library unless you explicitly export them there.
          </p>
        ),
      },
      {
        q: 'Can you recover a deleted clip for me?',
        a: (
          <p>
            No. The developer has no access to your device or its storage. Once a clip is
            permanently deleted (emptied from Trash), it cannot be recovered by anyone.
          </p>
        ),
      },
    ],
  },
  {
    id: 'legal',
    title: 'Legal',
    items: [
      {
        q: 'Can I use LumaDrive footage in a legal case?',
        a: (
          <p>
            Possibly, but this depends on your jurisdiction, how footage was obtained, and chain
            of custody requirements. Consult a qualified legal professional before relying on any
            footage in a legal matter. LumaDrive makes no guarantee that footage will be accepted
            as evidence.
          </p>
        ),
      },
      {
        q: 'Is it legal to record with a dashcam in my area?',
        a: (
          <p>
            Recording laws vary by country, state, and municipality — especially regarding audio
            recording of other people. You are solely responsible for knowing and complying with
            the laws in every jurisdiction where you use the app. LumaDrive does not provide legal
            advice.
          </p>
        ),
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    items: [
      {
        q: 'The camera shows a black screen.',
        a: (
          <p>
            Go to iOS Settings → Privacy &amp; Security → Camera and make sure LumaDrive has
            camera permission. If permission is already granted, force-quit the app and reopen it.
          </p>
        ),
      },
      {
        q: 'I see "Recording Paused" on screen.',
        a: (
          <p>
            This appears when iOS interrupts camera access — usually due to a phone call, another
            app using the camera, or a system-level interruption. Tap Dismiss on the overlay and
            tap Record again to resume.
          </p>
        ),
      },
      {
        q: 'The app crashes during recording.',
        a: (
          <p>
            Make sure your device has adequate free storage. If the problem persists, try
            restarting your iPhone before your next drive. If crashes continue, contact support
            with your device model and iOS version.
          </p>
        ),
      },
      {
        q: "GPS / speed isn't showing in the HUD.",
        a: (
          <p>
            Check iOS Settings → Privacy &amp; Security → Location Services → LumaDrive → set to
            "While Using". The GPS signal can also take up to 30 seconds to lock when outdoors.
          </p>
        ),
      },
    ],
  },
];

export default function Support() {
  return (
    <div className="legal-page">
      <header className="legal-nav">
        <Link to="/" className="legal-brand">
          <img src={lumadriveIcon} alt="" />
          <span>LumaDrive</span>
        </Link>
        <Link to="/" className="legal-back">
          ← Back to site
        </Link>
      </header>

      <main className="legal-wrap">
        <aside className="legal-toc" aria-label="Categories">
          <div className="legal-toc-inner">
            <span className="legal-toc-label">Categories</span>
            <ol>
              {categories.map((c, i) => (
                <li key={c.id}>
                  <a href={`#${c.id}`}>
                    <span className="legal-toc-num">{String(i + 1).padStart(2, '0')}</span>
                    <span>{c.title}</span>
                  </a>
                </li>
              ))}
            </ol>
            <a href="#contact" className="legal-toc-cross">
              Contact support →
            </a>
          </div>
        </aside>

        <article className="legal-doc">
          <div className="legal-hero">
            <span className="eyebrow">Support</span>
            <h1>How can we help?</h1>
            <p className="support-lede">
              Quick answers to the most common LumaDrive questions. Can't find what you need?{' '}
              <a href="#contact">Email us</a> — we usually reply within 2–3 business days.
            </p>
          </div>

          <div className="support-quicklinks">
            {categories.map((c) => (
              <a key={c.id} href={`#${c.id}`} className="support-chip">
                {c.title}
              </a>
            ))}
          </div>

          {categories.map((c, i) => (
            <section key={c.id} id={c.id} className="support-category">
              <h2>
                <span className="legal-h-num">{String(i + 1).padStart(2, '0')}</span>
                {c.title}
              </h2>
              <div className="support-list-faq">
                {c.items.map((it) => (
                  <details key={it.q} className="support-faq-item">
                    <summary>
                      <span className="support-q">{it.q}</span>
                      <span className="faq-icon" aria-hidden="true">
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </summary>
                    <div className="support-a">{it.a}</div>
                  </details>
                ))}
              </div>
            </section>
          ))}

          <section id="contact" className="support-contact">
            <div className="support-contact-card">
              <div className="support-contact-icon">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
              </div>
              <h2>Still need help?</h2>
              <p>
                Send us a note — please include your device model, iOS version, and a description
                of the issue.
              </p>
              <a className="support-email" href="mailto:mukuwabaffoe@gmail.com">
                mukuwabaffoe@gmail.com
              </a>
              <p className="support-fine">We aim to respond within 2–3 business days.</p>
            </div>
          </section>

          <div className="legal-footer">
            <Link to="/" className="legal-back-bottom">
              ← Back to LumaDrive
            </Link>
            <Link to="/terms" className="legal-back-bottom">
              View Terms of Use →
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
}
